import { useState, useMemo, useEffect, useCallback } from 'react';
import { autorun } from 'mobx';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { EventQueueView, QueueClassEnumType, QueueColumnFilter } from '@mark43/cad-api';
import { FilterDirective, ListStoreOptions } from '@list-store';
import { useEventNotificationsStore } from '../event-notifications/use-event-notifications';
import { EventQueueRowData } from '../socket/stream-types';
import { workspacePanelIsQueue } from '../panels/workspace-queue-exists';
import { useAedStore } from '../automatic-event-dispatch/use-aed-store';
import { EventQueueViewsListener, useEventQueueViews } from './event-queue-views-provider';
import { useWorkspacePanel } from '../panels/hooks/use-workspace-panel';
import { useRegisterQueueStore } from '../queues/queue-registry/use-register-queue-store';
import {
    filterEventQueueViewByDispatchArea,
    filterEventQueueViewByEventQueueType,
    filterQueueViewByQueueColumnFilters,
    filterQueueViewsByAssignmentStatus,
    filterQueueViewsByClass,
    filterQueueViewsBySearchValue,
    getQueueColumnFiltersByType,
} from '../queues/queue-filter-helpers';
import {
    alphabeticalStringComparator,
    alphanumericStringComparator,
} from '../common/sorting/comparators';
import columnDefinitionEnum from '../queues/column-definition.enum';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useDispatchAreaStore } from '../dispatch-areas/use-dispatch-areas';
import { EventQueueStore } from './store/event-queue-store';
import { useSyncWorkspacePanelToQueueStore } from '../queues/use-sync-workspace-panel-to-queue-store';
import { useFrEventsListAgencyTypeOptions } from '../fr-events-list-page/use-fr-events-list-agency-type-options';
import { globalAttributes } from '../common/global-attributes';
import { useUnitLocationStore } from '../fr-unit-location/use-unit-location-store';
import { WorkspaceQueue } from '../panels/workspace-queue';

function createFilterDirective(props: {
    filterQuery: string;
    dispatchAreaIds: number[];
    queueColumnFilters: QueueColumnFilter[];
    queueClasses: QueueClassEnumType[];
    eventQueueType: WorkspaceQueue['eventQueue'];
    assignmentStatus: undefined | boolean;
    isFirstResponder: boolean;
}): FilterDirective<EventQueueView> {
    const {
        dispatchAreaIds,
        filterQuery,
        queueColumnFilters,
        assignmentStatus,
        queueClasses,
        eventQueueType,
    } = props;
    const queueColumnFiltersByType = getQueueColumnFiltersByType(queueColumnFilters);
    return (item: EventQueueView) => {
        if (!filterEventQueueViewByEventQueueType(eventQueueType, item)) return false;
        if (!filterQueueViewsBySearchValue(item.searchValue, filterQuery)) return false;
        if (!filterQueueViewsByAssignmentStatus(item, assignmentStatus)) return false;
        if (!filterQueueViewsByClass(item, queueClasses)) return false;
        if (
            !props.isFirstResponder &&
            !filterEventQueueViewByDispatchArea(item, dispatchAreaIds, queueColumnFiltersByType)
        ) {
            return false;
        }
        if (
            !filterQueueViewByQueueColumnFilters(
                item.queueFiltersMetadata,
                queueColumnFiltersByType
            )
        )
            return false;
        return true;
    };
}

const FR_DEFAULT_SORTING_AND_GROUPING = {
    sortingDirective: {
        getSortValue: columnDefinitionEnum.PRIORITY.getSortValue,
        comparator: alphanumericStringComparator,
    },
    groupingDirective: {
        getGroupValue: columnDefinitionEnum.ASSIGNED_STATUS.getFlatValue,
        comparator: (left, right) => {
            const result = alphabeticalStringComparator(left, right);
            return result * -1;
        },
    },
} as ListStoreOptions<EventQueueRowData>;

/**
 * This should never be imported directly outside of event-queue-state-provider
 */
export function useIntegratedEventQueueStore() {
    const workspacePanel = useWorkspacePanel();
    workspacePanelIsQueue(workspacePanel);
    const { addListener, removeListener, eventQueueViewsRef } = useEventQueueViews();
    const enabledFeatures = useEnabledFeatures();
    const eventNotificationsStore = useEventNotificationsStore();
    const aedStore = useAedStore();
    const dispatchAreaStore = useDispatchAreaStore();
    const unitLocationStore = useUnitLocationStore();

    const agencyTypeOptions = useFrEventsListAgencyTypeOptions();

    const { isFirstResponder } = useApplicationType();

    const defaults = isFirstResponder ? FR_DEFAULT_SORTING_AND_GROUPING : {};

    const store = useState(
        () =>
            new EventQueueStore({
                manualFilterDirective: true,
                workspacePanel,
                eventNotificationsStore,
                playAedSoundForEvent: (eventId) => {
                    if (enabledFeatures.dispatchPlayAlertOnAedEvent) {
                        aedStore.playSoundForEvent(eventId);
                    }
                },
                sortingDirective: defaults.sortingDirective,
                groupingDirective: defaults.groupingDirective,
            })
    )[0];

    useSyncWorkspacePanelToQueueStore(workspacePanel, store);

    useRegisterQueueStore(store);

    useEffect(() => {
        return autorun(() => {
            store.listStore.setFilterDirective(
                createFilterDirective({
                    filterQuery: store.filterQuery,
                    dispatchAreaIds: dispatchAreaStore.monitoredAndOwnedDispatchAreaIds,
                    queueColumnFilters: workspacePanel.queue.eventQueueFilters,
                    queueClasses: isFirstResponder
                        ? workspacePanel.queue.queueClasses.filter((cls) =>
                              agencyTypeOptions.some((opt) => opt.value === cls)
                          )
                        : workspacePanel.queue.queueClasses,
                    assignmentStatus: workspacePanel.queue.assignmentStatus,
                    eventQueueType: workspacePanel.queue.eventQueue,
                    isFirstResponder,
                }) as FilterDirective<EventQueueRowData>
            );
        });
    }, [dispatchAreaStore, isFirstResponder, store, workspacePanel, agencyTypeOptions]);

    const filterEventsByAgencyType = useCallback(
        (events: EventQueueView[]) => {
            if (!isFirstResponder) {
                return events;
            }
            const isPoliceOptionActive = agencyTypeOptions.find(
                (option) => option.value === 'POLICE'
            );
            if (!isPoliceOptionActive) {
                return events.filter(
                    (item) =>
                        item.agencyTypeGlobalAttrId !== globalAttributes.agencyTypeGlobal.POLICE
                );
            }
            return events;
        },
        [agencyTypeOptions, isFirstResponder]
    );

    const listener: EventQueueViewsListener = useMemo(() => {
        return {
            onBootstrap() {
                const eventQueueViews = filterEventsByAgencyType(
                    Object.values(eventQueueViewsRef.current)
                );
                store.setRows(eventQueueViews, unitLocationStore?.unitLocation);
            },
            onUpdate(bundle) {
                const eventQueueViews = filterEventsByAgencyType(bundle.eventQueueViews);
                store.upsertRows(eventQueueViews, unitLocationStore?.unitLocation);
                if (bundle.clearedEventIds) {
                    store.removeRows(bundle.clearedEventIds);
                }
            },
        };
    }, [filterEventsByAgencyType, eventQueueViewsRef, store, unitLocationStore]);

    useEffect(() => {
        addListener?.(listener);
        return () => {
            removeListener?.(listener);
        };
    }, [addListener, listener, removeListener]);

    return { store };
}
